/**
 * Browser-side persistence for campaign artifacts — narratives, masked audits and
 * refusal records.
 *
 * Records carry alias bindings, and those hold display labels the Orchestrator
 * must never receive. Same rule as `compoundAliasStore`: never attach a record
 * from here to a wire snapshot, a submission or a decision.
 *
 * Its own database rather than a third store inside `bmaps_deputy_aliases`: the
 * two have unrelated lifetimes, and adding a store there would mean an upgrade
 * path over every existing user's alias data to persist something that can be
 * rebuilt from a session file.
 *
 * @module deputy/campaignArtifactStore
 */

const DB_NAME = 'bmaps_campaign_artifacts';
const DB_VERSION = 1;
const STORE_NAME = 'campaign_artifacts';

// `window` rather than `globalThis`, which the shared eslint config does not know
// about. Guarded because the no-DOM test runner has neither, and a store with no
// IndexedDB behind it degrades to in-memory rather than throwing.
function resolveIndexedDB(indexedDBOverride) {
    if (indexedDBOverride) return indexedDBOverride;
    if (typeof window !== 'undefined' && window.indexedDB) return window.indexedDB;
    return null;
}

function openDB(indexedDBOverride) {
    return new Promise((resolve, reject) => {
        const idb = resolveIndexedDB(indexedDBOverride);
        if (!idb || typeof idb.open !== 'function') {
            resolve(null);
            return;
        }

        try {
            const request = idb.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                }
            };
            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = () => reject(request.error);
        } catch (err) {
            reject(err);
        }
    });
}

function runStoreOperation(db, mode, fn) {
    return new Promise((resolve, reject) => {
        if (!db) {
            resolve(null);
            return;
        }
        try {
            const tx = db.transaction(STORE_NAME, mode);
            const store = tx.objectStore(STORE_NAME);
            const request = fn(store);
            let requestResult = null;
            if (request) {
                request.onsuccess = () => {
                    requestResult = request.result;
                };
                request.onerror = () => reject(request.error);
            }
            tx.oncomplete = () => resolve(request ? requestResult : true);
            tx.onerror = () => reject(tx.error);
        } catch (err) {
            reject(err);
        }
    });
}

export function createCampaignArtifactStore({ indexedDB: indexedDBOverride } = {}) {
    /** Newest first, the order the panel lists them in. */
    async function listArtifacts() {
        try {
            const db = await openDB(indexedDBOverride);
            const records = await runStoreOperation(db, 'readonly', (store) => store.getAll());
            if (!Array.isArray(records)) return [];
            return [...records].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));
        } catch (_) {
            return [];
        }
    }

    async function putArtifact(record) {
        if (!record || typeof record.id !== 'string' || !record.id) return false;
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(db, 'readwrite', (store) => store.put(record));
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    async function deleteArtifact(id) {
        if (!id) return false;
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(db, 'readwrite', (store) => store.delete(id));
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    async function clearArtifacts() {
        try {
            const db = await openDB(indexedDBOverride);
            const result = await runStoreOperation(db, 'readwrite', (store) => store.clear());
            return result !== null;
        } catch (_) {
            return false;
        }
    }

    return {
        listArtifacts, putArtifact, deleteArtifact, clearArtifacts,
    };
}

export const defaultCampaignArtifactStore = createCampaignArtifactStore();
